import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getExerciseProgressions, hasProgressionData, debugProgressionData } from '../../utils/progressions';
import { shouldShowProgressions, logProgressionDebug } from '../../config/progressionConfig';
import PlaceholderImage from '../PlaceholderImage';

const ProgressionSection = ({ exercise }) => {
  const [showProgressions, setShowProgressions] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [imageIndex, setImageIndex] = useState({});

  // Image cycling effect
  useEffect(() => {
    if (!hoveredCard) return;

    const interval = setInterval(() => {
      setImageIndex(prev => {
        const currentIndex = prev[hoveredCard] || 0;
        const progressions = getExerciseProgressions(exercise.id);
        const allExercises = [...(progressions?.easierAlternatives || []), ...(progressions?.harderProgressions || []), ...(progressions?.variations || [])];
        const currentExercise = allExercises.find(ex => ex.id === hoveredCard);
        
        if (currentExercise && currentExercise.images && currentExercise.images.length > 1) {
          return {
            ...prev,
            [hoveredCard]: (currentIndex + 1) % currentExercise.images.length
          };
        }
        return prev;
      });
    }, 1000); // Change image every 1 second

    return () => clearInterval(interval);
  }, [hoveredCard, exercise.id]);

  // Check if feature is enabled
  if (!shouldShowProgressions()) {
    return null;
  }

  // Check if exercise has progression data
  if (!hasProgressionData(exercise.id)) {
    return null;
  }

  const progressions = getExerciseProgressions(exercise.id);

  const handleToggle = () => {
    setShowProgressions(!showProgressions);
  };
  
  return (
    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-blue-900">
          Exercise Progressions
        </h3>
        <button
          onClick={handleToggle}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          {showProgressions ? 'Hide' : 'Show'} Progressions
        </button>
      </div>
      
      {showProgressions && (
        <div className="mt-4 space-y-4">
          {/* Easier Alternatives */}
          {progressions.easierAlternatives.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Easier Alternatives
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {progressions.easierAlternatives.map((alt) => (
                  <Link
                    key={alt.id}
                    to={`/exercise/${alt.id}`}
                    className="block bg-white border-2 border-green-200 rounded-lg p-3 hover:border-green-400 hover:shadow-md transition-all duration-200 group"
                    onClick={() => logProgressionDebug(`Clicked easier alternative: ${alt.name}`, { fromExercise: exercise.id, toExercise: alt.id })}
                    onMouseEnter={() => setHoveredCard(alt.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {alt.images && alt.images.length > 0 ? (
                          alt.images[0] === "placeholder.jpg" ? (
                            <PlaceholderImage 
                              className="w-full h-full"
                            />
                          ) : (
                            <img 
                              src={`/exercise-images/${alt.images[imageIndex[alt.id] || 0]}`} 
                              alt={alt.name}
                              className="w-full h-full object-cover transition-opacity duration-300"
                            />
                          )
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xs">📷</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-sm text-gray-900 group-hover:text-green-700 transition-colors line-clamp-2">
                          {alt.name}
                        </h5>
                        <p className="text-xs text-gray-500 mt-1">
                          {alt.equipment || 'Body only'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {alt.primaryMuscles && alt.primaryMuscles.length > 0 
                            ? alt.primaryMuscles[0] 
                            : 'Multiple muscles'
                          }
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Harder Progressions */}
          {progressions.harderProgressions.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Harder Progressions
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {progressions.harderProgressions.map((progression) => (
                  <Link
                    key={progression.id}
                    to={`/exercise/${progression.id}`}
                    className="block bg-white border-2 border-red-200 rounded-lg p-3 hover:border-red-400 hover:shadow-md transition-all duration-200 group"
                    onClick={() => logProgressionDebug(`Clicked harder progression: ${progression.name}`, { fromExercise: exercise.id, toExercise: progression.id })}
                    onMouseEnter={() => setHoveredCard(progression.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {progression.images && progression.images.length > 0 ? (
                          progression.images[0] === "placeholder.jpg" ? (
                            <PlaceholderImage 
                              className="w-full h-full"
                            />
                          ) : (
                            <img 
                              src={`/exercise-images/${progression.images[imageIndex[progression.id] || 0]}`} 
                              alt={progression.name}
                              className="w-full h-full object-cover transition-opacity duration-300"
                            />
                          )
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xs">📷</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-sm text-gray-900 group-hover:text-red-700 transition-colors line-clamp-2">
                          {progression.name}
                        </h5>
                        <p className="text-xs text-gray-500 mt-1">
                          {progression.equipment || 'Body only'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {progression.primaryMuscles && progression.primaryMuscles.length > 0 
                            ? progression.primaryMuscles[0] 
                            : 'Multiple muscles'
                          }
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Variations */}
          {progressions.variations.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Variations
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {progressions.variations.map((variation) => (
                  <Link
                    key={variation.id}
                    to={`/exercise/${variation.id}`}
                    className="block bg-white border-2 border-blue-200 rounded-lg p-3 hover:border-blue-400 hover:shadow-md transition-all duration-200 group"
                    onClick={() => logProgressionDebug(`Clicked variation: ${variation.name}`, { fromExercise: exercise.id, toExercise: variation.id })}
                    onMouseEnter={() => setHoveredCard(variation.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {variation.images && variation.images.length > 0 ? (
                          variation.images[0] === "placeholder.jpg" ? (
                            <PlaceholderImage 
                              className="w-full h-full"
                            />
                          ) : (
                            <img 
                              src={`/exercise-images/${variation.images[imageIndex[variation.id] || 0]}`} 
                              alt={variation.name}
                              className="w-full h-full object-cover transition-opacity duration-300"
                            />
                          )
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xs">📷</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-sm text-gray-900 group-hover:text-blue-700 transition-colors line-clamp-2">
                          {variation.name}
                        </h5>
                        <p className="text-xs text-gray-500 mt-1">
                          {variation.equipment || 'Body only'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {variation.primaryMuscles && variation.primaryMuscles.length > 0 
                            ? variation.primaryMuscles[0] 
                            : 'Multiple muscles'
                          }
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressionSection;
